import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Users, Megaphone, Mail as MailIcon, FileText, Calendar } from "lucide-react";

const STATUS_COLORS = {
  nuevo: "#8A8F98",
  calificando: "#E8825E",
  calificado: "#D95D39",
  agendado: "#0A1128",
  cotizado: "#2D6A4F",
  ganado: "#1B4332",
  perdido: "#9D0208",
};

export default function HomePage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[#5C616A]">Cargando...</div>;

  const tiles = [
    { label: "Leads", value: data.totals.leads, icon: Users },
    { label: "Campañas", value: data.totals.campaigns, icon: Megaphone },
    { label: "Artículos SEO", value: data.totals.articles, icon: FileText },
    { label: "Landings", value: data.totals.landings, icon: FileText },
    { label: "Reuniones", value: data.totals.meetings, icon: Calendar },
    { label: "Broadcasts", value: data.totals.broadcasts, icon: MailIcon },
  ];

  const statusData = Object.entries(data.leads_by_status).map(([k, v]) => ({ status: k, value: v }));
  const scoreData = Object.entries(data.leads_by_score).map(([k, v]) => ({ name: k, value: v }));
  const scoreColors = ["#D95D39", "#E8825E", "#8A8F98", "#E2DCD0"];

  return (
    <div className="stagger">
      <PageHeader
        overline="Control Room"
        title="Bienvenido al estudio."
        subtitle="Una visión editorial de todas tus campañas, leads, contenidos y citas."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        {tiles.map((t, i) => (
          <Card
            key={t.label}
            className="edit-card fade-up p-5 rounded-sm shadow-none"
            style={{ "--i": i }}
          >
            <div className="flex items-center justify-between mb-3">
              <t.icon className="w-4 h-4 text-[#8A8F98]" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">
                {t.label}
              </span>
            </div>
            <div className="font-mono text-3xl text-[#0A1128]">{t.value}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Card className="edit-card lg:col-span-2 p-6 rounded-sm shadow-none">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Pipeline</p>
              <h3 className="font-display text-2xl text-[#0A1128] mt-1">Leads por estado</h3>
            </div>
            <TrendingUp className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2DCD0" vertical={false} />
              <XAxis dataKey="status" tick={{ fill: "#5C616A", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#5C616A", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2DCD0", borderRadius: 2 }} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {statusData.map((entry, i) => (
                  <Cell key={i} fill={STATUS_COLORS[entry.status] || "#0A1128"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="edit-card p-6 rounded-sm shadow-none">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Calidad</p>
          <h3 className="font-display text-2xl text-[#0A1128] mt-1 mb-5">Score de leads</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={scoreData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                {scoreData.map((_, i) => (
                  <Cell key={i} fill={scoreColors[i % scoreColors.length]} />
                ))}
              </Pie>
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: "#5C616A" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-[#E2DCD0]">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">
                Conversión a ganado
              </span>
              <span className="font-mono text-2xl text-[#D95D39]">{data.conversion_rate}%</span>
            </div>
          </div>
        </Card>
      </div>

      <Card className="edit-card p-6 rounded-sm shadow-none">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Atribución</p>
        <h3 className="font-display text-2xl text-[#0A1128] mt-1 mb-5">Top fuentes de leads</h3>
        {data.leads_by_source.length === 0 ? (
          <p className="text-[#5C616A] text-sm">Aún no hay leads registrados.</p>
        ) : (
          <ul className="divide-y divide-[#E2DCD0]">
            {data.leads_by_source.map((s) => (
              <li key={s.source} className="flex items-center justify-between py-3">
                <span className="text-sm text-[#1A1F2B]">{s.source}</span>
                <span className="font-mono text-sm text-[#5C616A]">{s.count}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
